/**
 * One COMPACT game action (fix D / R5): a 36 px text+icon button, not M1a's
 * 60 px tile. The tiles were the loudest thing in the rail and they said the
 * least — a wrapping grid of four 60 px cards under a 400 px notation (R6.2).
 *
 * Stateless, so it is a `*.View.tsx`: the leaves in `buttons.tsx` bring the
 * models, which are M1a's tile models unchanged (they were correct).
 */
import React from 'react';
import {Pressable, StyleSheet} from 'react-native';

import Icon, {IconName} from '../../../components/Icon';
import {MOTION, RADIUS, SPACE} from '../../../theme/tokens';
import {useV2Theme} from '../../../theme/useV2Theme';
import {Txt} from '../../../ui';

const HEIGHT = 36;
const ICON_SIZE = 16;
const DISABLED_OPACITY = 0.4;

/** `danger` = resign / abort, `active` = a state the player is waiting in */
export type ActionTone = 'default' | 'danger' | 'active' | 'primary';

export type ActionButtonProps = {
  label: string;
  icon?: IconName;
  /** the ½ of the draw button, which is type and not an icon */
  glyph?: string;
  tone?: ActionTone;
  disabled?: boolean;
  onPress?: () => void;
  testID?: string;
};

export default React.memo(function ActionButtonView({
  label,
  icon,
  glyph,
  tone = 'default',
  disabled,
  onPress,
  testID,
}: ActionButtonProps) {
  const tokens = useV2Theme(s => s.tokens);

  const color =
    tone === 'danger'
      ? tokens.danger
      : tone === 'active'
      ? tokens.ev
      : tone === 'primary'
      ? tokens.onPri
      : tokens.ink2;
  const fill =
    tone === 'primary'
      ? tokens.pri
      : tone === 'active'
      ? tokens.evTint
      : 'transparent';

  return (
    <Pressable
      testID={testID}
      disabled={disabled}
      onPress={disabled ? undefined : onPress}
      accessibilityLabel={label}
      style={({hovered}: any) => [
        styles.button,
        {
          backgroundColor:
            hovered && !disabled && tone === 'default' ? tokens.well : fill,
          opacity: disabled ? DISABLED_OPACITY : 1,
          transitionProperty: 'background-color, opacity',
          transitionDuration: `${MOTION.ms}ms`,
          transitionTimingFunction: MOTION.easing,
        } as any,
      ]}>
      {glyph ? (
        <Txt variant="labelSm" color={color}>
          {glyph}
        </Txt>
      ) : icon ? (
        <Icon name={icon} size={ICON_SIZE} color={color} />
      ) : null}
      <Txt variant="labelSm" color={color} numberOfLines={1}>
        {label}
      </Txt>
    </Pressable>
  );
});

const styles = StyleSheet.create({
  button: {
    height: HEIGHT,
    borderRadius: RADIUS.control,
    paddingHorizontal: SPACE.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
});
